import { FormInstance, message } from 'antd';
import { playlistRuleService } from '../services/playlistRuleService';
interface ContentRuleHook {
  /** Resolves true only when the rules actually reached the server. */
  handleSave: () => Promise<boolean>;
}

interface ContentRuleHandlersProps {
  form: FormInstance;
  playlistId: number;
}

type RuleValueType = 'tag' | 'recent' | 'series' | 'season';

type Rule = {
  value_type: RuleValueType;
  value: string;
};

/**
 * Form fields that map onto a playlist rule, in the order the server sees them.
 *
 * `content` is absent on purpose: ContentRule writes that rule straight through
 * `upsert-rule` as items move, so including it here would let a save overwrite those moves
 * with the value the form happened to hold.
 */
const RULE_FIELDS: RuleValueType[] = ['recent', 'tag', 'series', 'season'];

/**
 * Rule values reach the form in a few shapes: id arrays from the transfer pickers, a plain
 * number/string from the Recent input, and (after a fetch) whatever `split(',')` produced.
 * They all have to end up as the comma-joined string `upsert-rule` expects.
 *
 * An **empty** result is meaningful: `value: ''` is how the server deletes a rule.
 */
const toRuleValue = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.filter(item => item !== null && item !== undefined && item !== '').join(',');
  }
  return String(value);
};

/** Pulls the field error text out of a rejected validateFields()/axios error. */
const describeSaveError = (error: unknown): string => {
  const fields = (error as { errorFields?: { errors?: string[] }[] })?.errorFields;
  if (Array.isArray(fields) && fields.length > 0) {
    return fields.flatMap(field => field.errors ?? []).join(' ') || 'Check the rule values.';
  }
  const data = (error as { response?: { data?: { message?: string; error?: string } } })?.response
    ?.data;
  return data?.message || data?.error || 'Failed to save the rules.';
};

export const playlistRuleHandlers = ({
  form,
  playlistId,
}: ContentRuleHandlersProps): ContentRuleHook => {
  const handleSave = async (): Promise<boolean> => {
    try {
      await form.validateFields();

      // Read the store directly rather than trusting validateFields()' return value: it only
      // reports *registered* fields, so anything that unmounted (or a form torn down by an
      // early navigation) silently yields {} and the save turns into a no-op.
      const values = form.getFieldsValue(true) as Record<string, unknown>;

      const rules: Rule[] = RULE_FIELDS.filter(
        field => values[field] !== undefined && values[field] !== null
      ).map(field => ({ value_type: field, value: toRuleValue(values[field]) }));

      await playlistRuleService.upsertPlaylistRule(playlistId, rules);
      message.success('Rule has been saved.');
      return true;
    } catch (error) {
      console.error('An error occurred while saving:', error);
      message.error(describeSaveError(error));
      return false;
    }
  };

  return {
    handleSave,
  };
};
